import React, { useEffect, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Trans, useTranslation } from "react-i18next";
import boost from "highcharts/modules/boost";
import Exporting from "highcharts/modules/exporting";
import * as _ from "lodash";
import SimpleNotification from "../../components/Notifications/SimpleNotification";
import { isNil } from "lodash";
import LoadingOverlay from "../Loaders/LoadingOverlay";
import SpectrumOptionsCollapse from "../Scans/SpectrumOptionsCollapse";

boost(Highcharts);
Exporting(Highcharts);

export default function SpectrumChart({
    points = [],
    deletedPoints,
    handleDeletedPoints,
    selectedRows,
    setSelectedRows,
    visible,
    setIsWavenumber,
    setIsReflectance,
    setXAxisCheckedReference,
    setYAxisCheckedReference,
    isWavenumber = true,
    isReflectance = true,
    isSpectrumDisabled = false,
    renderSwitch = true,
    enableSelectionButtons = true,
    hideBackground = false,
    tooltip = false,
}) {
    const [isLoading, setIsLoading] = useState(false);
    const [options, setOptions] = useState({});
    const [selectionModeEnabled, setselectionModeEnabled] = useState(false);
    const container = useRef();
    const render = useRef(false);
    const deletedIndecies = useRef([]);
    const timer = useRef(null);
    const selectionModeEnabledRef = useRef(false);
    const { t } = useTranslation();
    const [chartPoints, setChartPoints] = useState([]);

    useEffect(() => {
        setIsLoading(true);
        render.current = true;
        setTimeout(() => {
            setChartPoints(points);
        }, 100);

        () => {
            setChartPoints([]);
            setIsLoading(false);
        };
    }, [points]);

    useEffect(() => {
        if (chartPoints) {
            setIsLoading(false);
            render.current = false;
            setChart(chartPoints);
        }
    }, [chartPoints]);

    useEffect(() => {
        if (deletedPoints && selectedRows) {
            if (deletedPoints.length > 0) {
                setIsLoading(true);
                removeDeleted(deletedPoints);
            } else if (deletedIndecies.current.length > selectedRows.length) {
                // there is a deleted indecies
                const diff = _.difference(
                    deletedIndecies.current,
                    selectedRows
                );
                handleLineColor(diff, null);
            } else if (deletedIndecies.current.length < selectedRows.length) {
                // there is an added indecies
                const diff = _.difference(
                    selectedRows,
                    deletedIndecies.current
                );
                handleLineColor(diff, "#ff0000");
            }
            deletedIndecies.current = selectedRows;
        }
    }, [selectedRows]);

    const removeDeleted = (delPoints) => {
        let changed = false;
        for (let point of delPoints) {
            if (!render.current) {
                changed = true;
                container.current.chart.get(point.id).remove(false, false);
            }
        }
        handleDeletedPoints([]);
        if (changed && !timer.current) {
            timer.current = setTimeout(() => {
                container.current.chart.redraw();
                timer.current = null;
            }, 1000);
        }
    };

    const deleteLine = (id) => {
        let selected = [...deletedIndecies.current];
        const i = selected.indexOf(id);
        if (i < 0) {
            !isNil(id) && selected.push(id);
        } else {
            selected.splice(i, 1);
        }
        setSelectedRows([...selected]);
    };

    const handleLineColor = (rows, color) => {
        let changed = false;
        if (!container.current) return;
        for (const id of rows) {
            const line = container.current.chart?.get(id);
            if (line?.color !== color && !render.current) {
                changed = true;
                line?.update({ color }, false);
            }
        }

        if (changed && !timer.current) {
            timer.current = setTimeout(() => {
                container.current?.chart.redraw();
                timer.current = null;
            }, 1000);
        }
    };

    const handleReset = (event) => {
        event.preventDefault();
        handleLineColor(deletedIndecies.current, null);
        selectedRows && setSelectedRows([]);
    };

    const getLineColor = () => {
        return selectionModeEnabledRef.current ? "#000000" : "#ff0000";
    };

    const selectionFunction = (event) => {
        if (selectionModeEnabledRef.current && !event.resetSelection) {
            event.preventDefault();
            const xMin = event.xAxis[0].min,
                xMax = event.xAxis[0].max,
                yMin = event.yAxis[0].min,
                yMax = event.yAxis[0].max,
                chart = container.current.chart;
            let selectedLines = [];

            chart.series.forEach((series) => {
                series.xData.forEach((x, index) => {
                    const y = series.yData[index];
                    if (
                        x >= xMin &&
                        x <= xMax &&
                        y >= yMin &&
                        y <= yMax &&
                        series.color !== "#ff0000" &&
                        !selectedLines.includes(series?.userOptions.id)
                    ) {
                        selectedLines.push(series?.userOptions.id);
                    }
                });
            });

            if (selectedRows && selectedLines.length > 0) {
                setSelectedRows([...selectedLines, ...deletedIndecies.current]);
            }
        }
    };

    const setChart = (processedPoints) => {
        setOptions({
            chart: {
                zoomType: "xy",
                events: {
                    load: function () {
                        setIsLoading(false);
                        render.current = false;
                    },
                    redraw: function () {
                        setIsLoading(false);
                        render.current = false;
                    },
                    render: function () {
                        setIsLoading(false);
                        render.current = false;
                    },
                    selection: selectionFunction,
                },
            },
            xAxis: {
                title: {
                    text: isWavenumber
                        ? `${t("wavenumber")} (cm⁻¹)`
                        : `${t("wavelength")} (nm)`,
                },
                reversed: isWavenumber,
            },
            yAxis: [
                {
                    title: {
                        text: isReflectance
                            ? t("reflectance")
                            : `${t("absorbance")} (a.u)`,
                    },
                },
            ],

            title: {
                text: null,
            },
            colors: [
                "#dacbe8",
                "#efb66a",
                "#2fa222",
                "#fbb",
                "#3a4c8e",
                "#40bccc",
                "#8799e6",
                "#76d450",
                "#a3b6fb",
                "#92d5de",
                "#bb87e2",
                "#65576f",
                "#16cee6",
                "#5b7fff",
            ],
            series: processedPoints,
            exporting: {
                buttons: {
                    contextButton: {
                        menuItems: [
                            {
                                text: t("printChart"),
                                textKey: "printChart",
                                onclick: function () {
                                    this.print();
                                },
                            },
                            "separator",
                            {
                                text: t("downloadPNG"),
                                textKey: "downloadPNG",
                                onclick: function () {
                                    this.exportChart();
                                },
                            },
                            {
                                text: t("downloadPDF"),
                                textKey: "downloadPDF",
                                onclick: function () {
                                    this.exportChart({
                                        type: "application/pdf",
                                    });
                                },
                            },
                            {
                                text: t("downloadSVG"),
                                textKey: "downloadSVG",
                                onclick: function () {
                                    this.exportChart({
                                        type: "image/svg+xml",
                                    });
                                },
                            },
                        ],
                    },
                },
            },
            tooltip:
                typeof tooltip === "object" ? tooltip : { enabled: tooltip },
            credits: { enabled: false },
            plotOptions: {
                series: {
                    marker: {
                        enabled: false,
                    },

                    animation: false,
                    point: {
                        events: {
                            click: function ({ point }) {
                                if (isInteractiveChart()) {
                                    const lineColor = getLineColor();
                                    const color =
                                        point.series.options.color === lineColor
                                            ? null
                                            : lineColor;
                                    point.series.update({ color });
                                    selectedRows &&
                                        deleteLine(
                                            point?.series?.userOptions?.id
                                        );
                                }
                            },
                            mouseOver: function () {
                                if (isInteractiveChart() && this.series.halo) {
                                    this.series.halo
                                        .attr({
                                            class: "highcharts-tracker",
                                        })
                                        .toFront();
                                }
                            },
                        },
                    },
                    cursor: "pointer",

                    states: {
                        // hover: {
                        //   enabled: false,
                        // },
                        inactive: {
                            opacity: 1,
                        },
                    },
                },
            },
        });
    };

    const handleBoundries = (event) => {
        // boundries selection mode activated
        event.preventDefault();
        setselectionModeEnabled(!selectionModeEnabled);
        selectionModeEnabledRef.current = !selectionModeEnabled;
    };

    const renderChart = () => {
        if (!chartPoints?.length && !isSpectrumDisabled) {
            return (
                <p className="chart-placeholder">
                    <Trans i18nKey="noDataDisplayMessage">
                        Unable to find any records
                    </Trans>
                </p>
            );
        } else if (!isSpectrumDisabled) {
            return (
                <HighchartsReact
                    ref={container}
                    constructorType={"chart"}
                    highcharts={Highcharts}
                    options={options}
                />
            );
        } else {
            return (
                <SimpleNotification
                    className="m-0"
                    message="This chart displays up to 1,500 rows for optimized rendering!"
                />
            );
        }
    };

    const isInteractiveChart = () => {
        return (
            chartPoints?.length > 0 &&
            !isSpectrumDisabled &&
            enableSelectionButtons
        );
    };

    return (
        <>
            <div
                className={`tabs-content-items flex flex-col mb-5 ${
                    visible && "show"
                }`}
                id="spectrum-content-wrapper"
            >
                {!hideBackground ? (
                    <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow rounded bg-light-gray">
                        <LoadingOverlay active={isLoading}>
                            <div className="rounded-t mb-0 px-4 py-3 bg-transparent">
                                <div className="flex flex-wrap items-center">
                                    <div className="relative w-full max-w-full flex-grow flex-1">
                                        <div className="flex items-center justify-between">
                                            <h2 className="text-primary-blue text-xl font-semibold">
                                                <Trans i18nKey="nirMeasurements">
                                                    NIR Measurements
                                                </Trans>
                                            </h2>
                                        </div>
                                        {renderSwitch && (
                                            <SpectrumOptionsCollapse
                                                isWavenumber={isWavenumber}
                                                setIsWavenumber={
                                                    setIsWavenumber
                                                }
                                                isReflectance={isReflectance}
                                                setIsReflectance={
                                                    setIsReflectance
                                                }
                                                chartPoints={chartPoints}
                                                setIsLoading={setIsLoading}
                                                setXAxisCheckedReference={
                                                    setXAxisCheckedReference
                                                }
                                                setYAxisCheckedReference={
                                                    setYAxisCheckedReference
                                                }
                                            />
                                        )}
                                        {selectionModeEnabled && (
                                            <h6 className="uppercase text-primary-orange mb-1 text-xs font-semibold">
                                                <Trans i18nKey="multiSelectActivated">
                                                    Multi-Selection Mode
                                                    Activated
                                                </Trans>
                                            </h6>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="p-3 flex-auto h-400-px">
                                <div className="flex bg-white">
                                    <div className="min-h-full w-full">
                                        {renderChart()}
                                    </div>
                                </div>
                            </div>
                        </LoadingOverlay>
                    </div>
                ) : (
                    <div className="flex-auto h-400-px">
                        <div className="flex bg-white">
                            <div className="min-h-full w-full">
                                {renderChart()}
                            </div>
                        </div>
                    </div>
                )}

                {isInteractiveChart() && (
                    <div className="steps-btn flex items-center justify-end mt-4">
                        <button
                            onClick={handleBoundries}
                            className="default-orange-btn"
                        >
                            {selectionModeEnabled
                                ? t("done")
                                : t("multiselection")}
                        </button>
                        <button
                            onClick={handleReset}
                            className={`${
                                selectionModeEnabled && "hidden"
                            } default-transparent-btn`}
                        >
                            <Trans i18nKey="cancelSelection">
                                Cancel Selection
                            </Trans>
                        </button>
                    </div>
                )}
            </div>
        </>
    );
}
